import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingCart, Menu, X, Phone } from "lucide-react";
import { COMPANY } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/pricing", label: "Pricing" },
  { to: "/support", label: "Install & Support" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { totals } = useCart();
  const [open, setOpen] = React.useState(false);
  const location = useLocation();
  React.useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={COMPANY.logo} alt="KSPB" className="h-11 w-11 rounded-md object-contain bg-[#0A192F] p-1" />
            <div className="leading-tight">
              <div className="font-heading text-lg font-bold text-[#0A192F]">KSPB</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Enterprises LLP</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `nav-link text-sm font-medium ${isActive ? "active text-[#FF6D00]" : "text-slate-700 hover:text-[#0A192F]"}`
                }
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-4">
            <a
              href={`tel:${COMPANY.phone}`}
              className="hidden md:flex items-center gap-2 text-sm font-medium text-[#0A192F] hover:text-[#FF6D00] transition-colors"
              data-testid="nav-call-link"
            >
              <Phone className="w-4 h-4" /> {COMPANY.phoneDisplay}
            </a>
            <Link
              to="/cart"
              className="relative inline-flex items-center gap-2 px-4 h-11 rounded-full bg-[#0A192F] text-white hover:bg-[#112240] transition-colors"
              data-testid="nav-cart-link"
            >
              <ShoppingCart className="w-4 h-4" />
              <span className="text-sm font-medium">Cart</span>
              {totals.count > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#FF6D00] text-white text-[10px] font-semibold rounded-full h-5 w-5 flex items-center justify-center" data-testid="cart-count">
                  {totals.count}
                </span>
              )}
            </Link>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 rounded-md border border-slate-200" data-testid="nav-menu-toggle">
                  {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-white">
                <div className="mt-8 flex flex-col gap-1">
                  {NAV.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.to === "/"}
                      className={({ isActive }) =>
                        `px-4 py-3 rounded-md text-base font-medium ${isActive ? "bg-[#0A192F] text-white" : "text-slate-800 hover:bg-slate-100"}`
                      }
                      data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    >
                      {n.label}
                    </NavLink>
                  ))}
                  <a
                    href={`tel:${COMPANY.phone}`}
                    className="mt-4 px-4 py-3 rounded-md bg-[#FF6D00] text-white font-semibold flex items-center gap-2"
                  >
                    <Phone className="w-4 h-4" /> Call {COMPANY.phoneDisplay}
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
